import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import {
	ufoEventOnboardingWizardProjectCreation,
	ufoEventOnboardingWizardProjectCreationIncludingProvisioning,
} from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { JIRA_AI_NATIVE_SIGNUP_SOURCE } from '@/components/user-context/helpers/cohorts';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { WizardCreateProjectPayload } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useCloudStatus } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status';
import { useCloudStatusRedirection } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status-redirection';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import {
	getProductQueryParamTeamType,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
} from '../../../models/user-segmentation';
import { getJtbdTemplateOverrideQueryParam } from '../../../utils/utils';
import { ANALYTICS_PREFIX } from '../common/constants';
import { useWizardConfiguration } from '../controllers';
import {
	useIsCodingAgentChoice,
	useIsGenericSampleData,
	useIsJfindOnboardIllustrationRefresh,
} from '../hooks/experiment-hooks';
import { onRedirectFailure } from '../utils/redirect-logging';
import { ProjectConfig } from '../utils/types';

import { useInviteStepFetchInterceptor } from './invite-step/hooks/use-invite-step-fetch-interceptor';
import { JiraOverlayShim } from './jira-overlay-shim';
import { WelcomeModalPageSwitcher } from './page-switcher/page-switcher';

const setProjectCreateTaskIdQueryParam = (taskId: string) => {
	const url = new URL(window.location.href);

	if (url.searchParams.get('taskId') === null) {
		url.searchParams.set('taskId', taskId);
		window.history.replaceState(null, '', url.toString());
		window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
	}
};

export const WelcomeModal = ({
	onComplete,
}: {
	onComplete: (taskId?: string, onRedirect?: () => void) => void;
}) => {
	// HOOKS
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { createCloudStatus, cloudId } = useCloudStatus();
	const isCreateRequestInProgress = useRef(false);
	const { signupId, taskIdParam, codingAgents, signupSource, jtbd, templateChoice } =
		useSearchParameters();
	const stargateClient = useBxpWebStargateClient();
	const { teamType, projectDetails } = useWizardConfiguration();
	const { codingAgentChoiceIsEnrolledInTest } = useIsCodingAgentChoice();
	const { genericSampleDataIsEnabled } = useIsGenericSampleData(projectDetails.template);
	const { deferRedirectView, setDeferRedirectView } = useCloudStatusRedirection();
	const [hasFiredProjectCreate, setHasFiredProjectCreate] = useState(false);
	const isIllustrationRefresh = useIsJfindOnboardIllustrationRefresh();
	const invitesRefactorEnabled = checkFeatureFlagGate(
		featureFlags.jiraOnboardingWizardRefactorInvites,
	);

	// STATE
	const [projectConfig, setProjectConfig] = useState<ProjectConfig | null>(null);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: `${ANALYTICS_PREFIX}Screen`,
			attributes: {
				teamType: teamType,
				template: projectDetails.template,
			},
		}).fire();
	}, [createAnalyticsEvent, projectDetails.template, teamType]);

	// Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
	useEffect(() => {
		if (!deferRedirectView) {
			if (setDeferRedirectView) {
				setDeferRedirectView('jira-onboarding-wizard');
			}
		}
	});

	// eslint-disable-next-line complexity
	const createProject = useCallback(async () => {
		if (projectConfig && cloudId) {
			const shouldForceSoftwareDevelopmentAgenticTemplate =
				signupSource === JIRA_AI_NATIVE_SIGNUP_SOURCE;

			ufoEventOnboardingWizardProjectCreation.start();

			ufoEventOnboardingWizardProjectCreation.setCustomData({ signupId });

			ufoEventOnboardingWizardProjectCreationIncludingProvisioning.startSubstep(
				UfoCustomTimingKey.ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL,
			);

			// Treatment was 100% rolled out: SCRUM and Software+KANBAN users always get GENERICV2.
			// Eligibility mirrors the original experiment targeting: the selected template
			// (lowercase 'scrum'/'kanban') and teamType, matching the deleted controller logic.
			//
			// The template arrives under different query params depending on the signup route.
			// The template choice screen sets `templateChoice`, but the JTBD route skips that
			// screen entirely (see jira-screen-config.tsx, where TEMPLATE_CHOICE is only visible
			// when `jtbd` is undefined) and instead records the selection in
			// `jtbdTemplateOverride`. Both must be checked or JTBD users are never eligible.
			const selectedTemplate = templateChoice ?? getJtbdTemplateOverrideQueryParam();

			const isEligibleForGenericV2 =
				selectedTemplate === 'scrum' ||
				(teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE && selectedTemplate === 'kanban');

			const sampleDataVersion =
				codingAgentChoiceIsEnrolledInTest || shouldForceSoftwareDevelopmentAgenticTemplate
					? 'SPACE_ONBOARDING'
					: genericSampleDataIsEnabled
						? 'GENERIC'
						: isEligibleForGenericV2
							? 'GENERICV2'
							: undefined;

			// Parse the coding agents selected on the coding agent choice
			// screen (persisted as a comma-separated `coding_agents` query
			// param, where "NONE" indicates an explicit empty selection).
			const parsedAgentTools =
				codingAgentChoiceIsEnrolledInTest && codingAgents && codingAgents !== 'NONE'
					? codingAgents
							.split(',')
							.map((agent) => agent.trim())
							.filter(Boolean)
					: [];
			const selectedAgentTools = parsedAgentTools.length > 0 ? parsedAgentTools : undefined;

			try {
				isCreateRequestInProgress.current = true;

				const projectPayload: WizardCreateProjectPayload = {
					projectName: projectConfig.projectName,
					issueTypes: projectConfig.issueTypes,
					statusNames: projectConfig.columns,
					projectKey: projectDetails.projectKey,
					template: projectDetails.template,
					cloudId: cloudId,
					teamType: getProductQueryParamTeamType(teamType),
					...(selectedAgentTools ? { selectedAgentTools } : {}),
					customDetail: {
						enableExtraFields: true,
						...(sampleDataVersion ? { sampleDataVersion } : {}),
						...(codingAgentChoiceIsEnrolledInTest || shouldForceSoftwareDevelopmentAgenticTemplate
							? { enableCardCover: true }
							: {}),
					},
				};
				const { taskId } = await stargateClient.wizardProjectCreate(projectPayload);

				// set the taskId if not already present, read by projectCreateTaskPolling to determine projectId
				setProjectCreateTaskIdQueryParam(taskId);
				setHasFiredProjectCreate(true);

				ufoEventOnboardingWizardProjectCreation.success({
					customData: {
						template: projectDetails.template,
						teamType: getProductQueryParamTeamType(teamType),
						sampleDataVersion,
					},
				});

				createAnalyticsEvent({
					action: 'created',
					actionSubject: `${ANALYTICS_PREFIX}Project`,
					eventType: 'track',
					attributes: {
						template: projectDetails.template,
						teamType,
						sampleDataVersion,
						jtbd,
						templateChoice,
					},
				}).fire();
			} catch (error) {
				ufoEventOnboardingWizardProjectCreation.failure({
					customData: {
						template: projectDetails.template,
						teamType: getProductQueryParamTeamType(teamType),
						sampleDataVersion,
					},
					error,
				});

				createAnalyticsEvent({
					action: 'failed',
					actionSubject: `${ANALYTICS_PREFIX}Project`,
					eventType: 'track',
					attributes: {
						template: projectDetails.template,
						teamType,
						sampleDataVersion,
						jtbd,
						templateChoice,
					},
				}).fire();

				// set the taskId if not already present to increment current page, Invite Step will show partial experience
				setProjectCreateTaskIdQueryParam('');
				setHasFiredProjectCreate(true);

				onRedirectFailure(projectDetails.template, error);
			}
		}
	}, [
		createAnalyticsEvent,
		projectConfig,
		cloudId,
		signupId,
		codingAgentChoiceIsEnrolledInTest,
		codingAgents,
		signupSource,
		genericSampleDataIsEnabled,
		projectDetails.projectKey,
		projectDetails.template,
		teamType,
		jtbd,
		templateChoice,
		stargateClient,
	]);

	useEffect(() => {
		// Only trigger creating project once the site is ready
		const canCreateProject =
			projectConfig && createCloudStatus === 'success' && cloudId && !taskIdParam;

		if (canCreateProject && !isCreateRequestInProgress.current) {
			createProject();
		}
	}, [createCloudStatus, cloudId, createProject, projectConfig, taskIdParam]);

	useEffect(() => {
		if (createCloudStatus === 'success') {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'succeeded',
				actionSubject: 'provisioning',
				actionSubjectId: 'jiraAnimatedProvisioningScreen',
				source: '/ondemand/signup/confirmation',
				attributes: {
					atlasId: 'ATLAS-56326',
					reactComponent: 'WelcomeModal',
					template: projectDetails.template,
					teamType: teamType,
				},
			}).fire();
		}
	}, [createAnalyticsEvent, createCloudStatus, projectDetails.template, teamType]);

	useInviteStepFetchInterceptor({ cloudId, isEnabled: !invitesRefactorEnabled });

	return (
		<FeatureExposed experimentKey="jfind-1565-sign-up-flow-overlay" group="Test">
			<JiraOverlayShim removePadding={isIllustrationRefresh}>
				<WelcomeModalPageSwitcher
					setProjectConfig={setProjectConfig}
					onComplete={onComplete}
					hasFiredProjectCreate={hasFiredProjectCreate}
					isIllustrationRefresh={isIllustrationRefresh}
				/>
			</JiraOverlayShim>
		</FeatureExposed>
	);
};
